import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { PackageSearch } from 'lucide-react';
import { cn } from '@/lib/utils/cn';
import type { CostBreakdown } from '../types/import-tracking.types';

/**
 * Shows cost components as a breakdown bar and detail list.
 * Each segment of the bar represents a cost category proportionally.
 */

interface CostBreakdownCardProps {
  costs: CostBreakdown | null;
  isLoading?: boolean;
  className?: string;
  currency?: string;
}

interface CostRow {
  label: string;
  amount: number;
  color: string;
}

function formatMoney(value: number, currency = 'USD'): string {
  return new Intl.NumberFormat('es-BO', {
    style: 'currency',
    currency,
    minimumFractionDigits: 2,
  }).format(value);
}

export function CostBreakdownCard({
  costs,
  isLoading,
  className,
  currency = 'USD',
}: CostBreakdownCardProps) {
  if (isLoading) {
    return (
      <Card className={className}>
        <CardHeader>
          <CardTitle>Desglose de Costos</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <Skeleton className="h-6 w-full rounded-full" />
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="flex justify-between">
              <Skeleton className="h-4 w-28" />
              <Skeleton className="h-4 w-20" />
            </div>
          ))}
        </CardContent>
      </Card>
    );
  }

  /*
    Sin productos NO hay costo nacionalizado, y decirlo importa.

    🔴 La pantalla mostraba "Costo Total Nacionalizado USD 12.879,41" con el FOB
    en cero: le cobraba IVA al flete de una importación que no existe. Un total
    con esa autoridad, sobre una orden vacía, se lee como un dato.

    Nacionalizar es traer MERCADERÍA. Sin ítems no hay valor en aduana, no hay
    partida, no hay arancel — y el flete solo no es un costo nacionalizado.
  */
  if (costs && costs.fob === 0) {
    return (
      <Card className={className}>
        <CardHeader>
          <CardTitle>Desglose de Costos</CardTitle>
          <CardDescription>
            El costo nacionalizado se calcula sobre la mercadería, y esta orden todavía no tiene
            productos.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col items-center gap-2 py-8 text-center">
            <PackageSearch className="h-9 w-9 text-prixia-silver" aria-hidden />
            <p className="text-sm font-medium">Cargá los productos</p>
            <p className="max-w-sm text-xs leading-relaxed text-muted-foreground">
              En la pestaña <strong>Productos</strong>, subiendo la proforma o uno por uno. Con la
              partida de cada uno, acá vas a ver el CIF, el arancel y el IVA.
            </p>
            {costs.freight > 0 && (
              <p className="mt-2 rounded-md bg-muted px-3 py-1.5 text-[11px] text-muted-foreground">
                El flete ya está cotizado en{' '}
                <strong className="font-mono">
                  {currency} {costs.freight.toLocaleString('es-BO', { minimumFractionDigits: 2 })}
                </strong>{' '}
                y se va a repartir entre los productos.
              </p>
            )}
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!costs) {
    return (
      <Card className={className}>
        <CardHeader>
          <CardTitle>Desglose de Costos</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-muted-foreground text-center py-4">Sin datos de costos</p>
        </CardContent>
      </Card>
    );
  }

  const otherTotal = costs.otherCosts.reduce((sum, c) => sum + c.amount, 0);

  const rows: CostRow[] = [
    { label: 'FOB', amount: costs.fob, color: 'bg-info' },
    { label: 'Flete', amount: costs.freight, color: 'bg-alerta' },
    { label: 'Seguro', amount: costs.insurance, color: 'bg-ok' },
    { label: 'Aranceles', amount: costs.duties, color: 'bg-bloqueo' },
    { label: 'Impuestos (IVA)', amount: costs.taxes, color: 'bg-purple-500' },
    { label: 'Internación', amount: costs.internacion, color: 'bg-info' },
  ];

  if (otherTotal > 0) {
    rows.push({ label: 'Otros', amount: otherTotal, color: 'bg-muted-foreground' });
  }

  const total = costs.totalLandedCost || rows.reduce((s, r) => s + r.amount, 0);

  return (
    <Card className={className}>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle>Desglose de Costos</CardTitle>
          <span className="text-lg font-bold text-primary">{formatMoney(total, currency)}</span>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Stacked bar */}
        <div className="flex h-5 rounded-full overflow-hidden bg-muted">
          {rows.map((row) => {
            const pct = total > 0 ? (row.amount / total) * 100 : 0;
            if (pct < 0.5) return null;
            return (
              <div
                key={row.label}
                className={cn('h-full transition-all', row.color)}
                style={{ width: `${pct}%` }}
                title={`${row.label}: ${pct.toFixed(1)}%`}
              />
            );
          })}
        </div>

        {/* Detail list */}
        <div className="space-y-2">
          {rows.map((row) => {
            const pct = total > 0 ? (row.amount / total) * 100 : 0;
            return (
              <div key={row.label} className="flex items-center justify-between text-sm">
                <div className="flex items-center gap-2">
                  <div className={cn('w-3 h-3 rounded-full', row.color)} />
                  <span>{row.label}</span>
                </div>
                <div className="flex items-center gap-3">
                  {/*
                    Este porcentaje es la PARTICIPACIÓN en el total, no la tasa.

                    Se rotula porque generaba una confusión concreta: el IVA daba
                    "13,0 %" —su parte del costo— justo al lado de un impuesto
                    cuya tasa efectiva es 14,943 %. Dos números parecidos que
                    significan cosas distintas, sin nada que los distinga.
                  */}
                  <span
                    className="text-xs text-muted-foreground"
                    title={`${pct.toFixed(1)} % del costo total — no es la tasa del tributo`}
                  >
                    {pct.toFixed(1)} % del total
                  </span>
                  <span className="font-medium tabular-nums w-28 text-right">
                    {formatMoney(row.amount, currency)}
                  </span>
                </div>
              </div>
            );
          })}
        </div>

        {/* Divider + total */}
        <div className="border-t pt-2 flex items-center justify-between font-semibold text-sm">
          <span>Costo Total Nacionalizado</span>
          <span className="tabular-nums">{formatMoney(total, currency)}</span>
        </div>

        {/* Other costs detail */}
        {costs.otherCosts.length > 0 && (
          <div className="border-t pt-2 space-y-1">
            <p className="text-xs text-muted-foreground font-medium">Otros costos:</p>
            {costs.otherCosts.map((c) => (
              <div key={c.id} className="flex justify-between text-xs text-muted-foreground">
                <span>{c.concept}</span>
                <span className="tabular-nums">{formatMoney(c.amount, currency)}</span>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
